import React from 'react';
import { useTranslation } from 'react-i18next';
import SeoMeta from '../components/SeoMeta';
import { PageHero, SectionIntro, ClosingCta, CheckList, faqSchema, usePrimaryCta } from '../components/common/B2B';
import { Section, ItemsSection, FlowSection, FaqSection, HeroChecks, TextLink } from '../components/common/B2BSections';

// Proveedores de software (nov-2026), contenido compuesto desde cero (Jesús:
// «siéntete libre de componer nuevos contenidos»): tres formas de integrarlo,
// una llamada real a la API (POST /apiCreateDocument de la Customer API, con los
// datos ficticios de los documentos de muestra), lo que no tienen que construir,
// cómo trabajamos con integradores y FAQ. Las capacidades salen del OpenAPI del
// portal (developer-portal/src/data/openapi.json); lettre de voiture fuera de la
// lista hasta que esté desplegada. Nada del cómo del ADR (pioneros).
// El formulario propio se sustituye por la reserva común con
// ?origin=integracion&profile=software.
// Hero: equipo de desarrollo (GPT Image 2, marketing/prompts-imagenes/hero-software-desarrolladores.txt).
// Versión inglesa (COPY.en) añadida el 4-nov-2026; la muestra de la API es la misma en los dos idiomas.

const PAGE_URL = { es: 'https://www.bluecmr.com/proveedores-software', en: 'https://www.bluecmr.com/en/software-providers' };
const QUERY = '?origin=integracion&profile=software';
const DEVELOPER_PORTAL = { es: '/es/developers/info', en: '/en/developers/info' };

const REQUEST = `x-api-key: ••••••••••••••••
Idempotency-Key: pedido-48213

{
  "modelId": "CARTA_DE_PORTE",
  "orderReference": "48213",
  "consignment": {
    "consignorParty": { "name": "Cerámicas Vallterra, S.L.", … },
    "consigneeParty": { "name": "Materiales Tajo Centro, S.L.", … },
    "carrierParty": { "name": "Transportes Maestrazgo Ruta, S.L.", … },
    "includedConsignmentItem": [ … ]
  }
}`;

const RESPONSE = `{
  "success": true,
  "data": {
    "documentId": "S7GX0KIE",
    "status": "active",
    "pdfUrl": "https://docs.bluecmr.com/2026/11/04/S7GX0KIE.pdf"
  }
}`;

const COPY = {
  es: {
    seoTitle: 'API de documentos de transporte para TMS y ERP | BlueCMR',
    seoDescription: 'Añada a su TMS o ERP la emisión de eCMR, carta de porte y DeCA con validez legal: API REST, webhooks y entorno de pruebas, con la firma resuelta.',
    seoKeywords: 'api ecmr, api carta de porte, api deca, integración tms documentos de transporte, webhooks transporte, api documentos de transporte efti',
    heroEyebrow: 'Para proveedores de software',
    heroTitle: 'Documentos de transporte con validez legal, dentro de su software',
    heroText: 'Para TMS, ERP, plataformas logísticas y apps de flota: sus clientes emiten y firman eCMR, cartas de porte y DeCA sin salir de su producto, y BlueCMR se ocupa del formato, la firma, la conservación y los cambios de la norma.',
    heroChecks: ['API REST y webhooks', 'Entorno de pruebas', 'Modelo de datos eFTI'],
    heroSecondary: 'Portal de desarrolladores',
    ways: {
      eyebrow: 'Integración',
      title: 'Tres formas de llevarlo a su producto',
      items: [
        { title: 'API REST y webhooks', text: 'Su software crea, modifica, envía y consulta documentos, y recibe al momento cada firma o evento.' },
        { title: 'Enlaces embebidos', text: 'La firma y la consulta del documento, dentro de sus apps, PDA o portal de clientes.' },
        { title: 'Marca blanca', text: 'Dominio, logotipo, colores y tipografía propios, y visor embebido en su web.' },
      ],
    },
    apiEyebrow: 'API',
    apiTitle: 'Una llamada, un documento',
    apiText: 'El modelo de datos sigue el conjunto común de datos de eFTI y la semántica de UN/CEFACT: las mismas partes, mercancías y lugares que ya maneja su software.',
    apiItems: [
      'Crear documentos uno a uno o por lotes, modificarlos y activar borradores',
      'Enviarlos a firma y consultar su estado',
      'Añadir eventos y adjuntos durante el transporte',
      'Archivar documentos de otras empresas',
      'Cargar datos maestros de contactos en bloque',
      'Obtener el informe de evidencias de cada documento',
      'Webhooks configurables por evento',
    ],
    apiLink: 'Referencia de la API',
    built: {
      eyebrow: 'Resuelto por BlueCMR',
      title: 'Lo que no tiene que construir',
      items: [
        { title: 'El formato de cada documento', text: 'eCMR, carta de porte, DeCA, documento ADR y albarán, con los datos que exige cada norma, validados antes de emitir.' },
        { title: 'Los cambios de la norma', text: 'Las adaptaciones a cambios normativos, como la Resolución de 5 de junio de 2026 o la llegada de eFTI, corren de nuestra cuenta.' },
        { title: 'La firma electrónica', text: 'Biométrica con nivel de firma avanzada, con sello o con OTP, para firmantes que no tienen cuenta ni instalan nada.' },
        { title: 'Los envíos a los firmantes', text: 'Por correo, SMS o WhatsApp, con un acceso único y revocable para cada uno.' },
        { title: 'La conservación', text: 'PDF firmado en PAdES-LTV, huellas criptográficas y sellos de tiempo, y acceso para las partes y las autoridades.' },
        { title: 'La seguridad', text: 'Datos en la Unión Europea. BLUECMR, S.L.U. dispone de un sistema de gestión certificado de acuerdo al RD 311/2022 (ENS) por OCA CERT.' },
      ],
    },
    process: {
      eyebrow: 'Cómo trabajamos',
      title: 'De la primera conversación a producción',
      steps: [
        { place: 'Inicio', title: 'Conversación técnica', text: 'Vemos su producto, sus clientes y qué documentos necesitan.' },
        { place: 'Desarrollo', title: 'Entorno de pruebas', text: 'Claves de pruebas, referencia de la API, guías por modelo y ejemplos en el portal de desarrolladores.' },
        { place: 'Piloto', title: 'Con un cliente real', text: 'Antes de abrirlo a todos, lo probamos con uno de sus clientes en su operativa.' },
        { place: 'Producción', title: 'Y después', text: 'Claves de producción y soporte técnico para su equipo.' },
      ],
    },
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Lo que suelen preguntarnos los integradores',
    faq: [
      { q: '¿Qué documentos se pueden emitir por API?', a: 'eCMR, carta de porte, DeCA, documento ADR con DeCA y albarán con DeCA. La propia API devuelve los modelos disponibles en cada cuenta.' },
      { q: '¿Hay entorno de pruebas?', a: 'Sí: la misma API en un entorno de pruebas separado de producción, con sus propias claves.' },
      { q: '¿Sus clientes tienen que salir de su aplicación?', a: 'No. Los documentos se crean y se envían desde su software; el firmante recibe un enlace por correo, SMS o WhatsApp, o firma en una vista embebida en su app o portal.' },
      { q: '¿Qué pasa si una petición se reintenta?', a: 'Con la cabecera Idempotency-Key, el reintento devuelve el documento ya creado, sin duplicarlo ni consumir otro crédito.' },
      { q: '¿Cómo podemos colaborar?', a: 'Depende de su producto y de sus clientes: integrarlo en su software, ofrecerlo en marca blanca u otras fórmulas. Lo vemos juntos en una primera conversación.' },
      { q: '¿Dónde está la documentación?', a: 'En el portal de desarrolladores: referencia de la API, guías por modelo y ejemplos.' },
    ],
    closing: 'Hablemos de su integración',
    closingText: 'Una conversación técnica sobre su producto y sus clientes, sin compromiso.',
    breadcrumb: ['Inicio', 'Proveedores de software'],
  },
  en: {
    seoTitle: 'Transport document API for TMS and ERP | BlueCMR',
    seoDescription: 'Add eCMR, consignment note and DeCA issuing to your TMS or ERP with full legal validity: REST API, webhooks and a sandbox, with signatures handled.',
    seoKeywords: 'ecmr api, consignment note api, deca api, control document api, tms transport document integration, transport webhooks, efti transport document api',
    heroEyebrow: 'For software providers',
    heroTitle: 'Legally valid transport documents, inside your software',
    heroText: 'For TMS, ERP, logistics platforms and fleet apps: your customers issue and sign eCMRs, consignment notes and control documents without leaving your product, and BlueCMR takes care of format, signature, preservation and regulatory changes.',
    heroChecks: ['REST API and webhooks', 'Sandbox', 'eFTI data model'],
    heroSecondary: 'Developer portal',
    ways: {
      eyebrow: 'Integration',
      title: 'Three ways to bring it into your product',
      items: [
        { title: 'REST API and webhooks', text: 'Your software creates, modifies, sends and queries documents, and receives every signature or event as it happens.' },
        { title: 'Embedded links', text: 'Signing and viewing the document, inside your apps, handhelds or customer portal.' },
        { title: 'White label', text: 'Your own domain, logo, colours and typography, and an embedded viewer on your website.' },
      ],
    },
    apiEyebrow: 'API',
    apiTitle: 'One call, one document',
    apiText: 'The data model follows the eFTI common data set and UN/CEFACT semantics: the same parties, goods and places your software already handles.',
    apiItems: [
      'Create documents one by one or in batches, modify them and activate drafts',
      'Send them for signature and check their status',
      'Add events and attachments during transport',
      'Archive documents from other companies',
      'Bulk-load contact master data',
      'Get the evidence report for each document',
      'Webhooks configurable per event',
    ],
    apiLink: 'API reference',
    built: {
      eyebrow: 'Handled by BlueCMR',
      title: 'What you do not have to build',
      items: [
        { title: 'The format of each document', text: 'eCMR, consignment note, control document, ADR transport document and delivery note, with the data each regulation requires, validated before issue.' },
        { title: 'Regulatory changes', text: 'We take care of adapting to regulatory changes, such as the Resolution of 5 June 2026 or the arrival of eFTI.' },
        { title: 'Electronic signature', text: 'Biometric at advanced electronic signature level, with a seal or with OTP, for signers who have no account and install nothing.' },
        { title: 'Sending to signers', text: 'By email, SMS or WhatsApp, with a unique, revocable access for each one.' },
        { title: 'Preservation', text: 'Signed PDF in PAdES-LTV, cryptographic hashes and timestamps, and access for the parties and the authorities.' },
        { title: 'Security', text: 'Data in the European Union. BLUECMR, S.L.U. holds a management system certified under Royal Decree 311/2022 (ENS) by OCA CERT.' },
      ],
    },
    process: {
      eyebrow: 'How we work',
      title: 'From the first conversation to production',
      steps: [
        { place: 'Start', title: 'Technical conversation', text: 'We look at your product, your customers and the documents they need.' },
        { place: 'Development', title: 'Sandbox', text: 'Test keys, API reference, guides per model and examples on the developer portal.' },
        { place: 'Pilot', title: 'With a real customer', text: 'Before opening it to everyone, we test it with one of your customers in their operations.' },
        { place: 'Production', title: 'And afterwards', text: 'Production keys and technical support for your team.' },
      ],
    },
    faqEyebrow: 'FAQ',
    faqTitle: 'What integrators usually ask us',
    faq: [
      { q: 'Which documents can be issued through the API?', a: 'eCMR, consignment note, control document (DeCA), ADR transport document with control document and delivery note with control document. The API itself returns the models available in each account.' },
      { q: 'Is there a sandbox?', a: 'Yes: the same API in a sandbox separate from production, with its own keys.' },
      { q: 'Do your customers have to leave your application?', a: 'No. Documents are created and sent from your software; the signer receives a link by email, SMS or WhatsApp, or signs in an embedded view in your app or portal.' },
      { q: 'What happens if a request is retried?', a: 'With the Idempotency-Key header, the retry returns the document already created, without duplicating it or using another credit.' },
      { q: 'How can we work together?', a: 'It depends on your product and your customers: integrating it into your software, offering it as white label or other arrangements. We look at it together in a first conversation.' },
      { q: 'Where is the documentation?', a: 'On the developer portal: API reference, guides per model and examples.' },
    ],
    closing: 'Let\'s talk about your integration',
    closingText: 'A technical conversation about your product and your customers, with no commitment.',
    breadcrumb: ['Home', 'Software providers'],
  },
};

/** Petición y respuesta reales de la API, en un panel oscuro. */
const ApiSample = () => (
  <figure className="bg-[#0b1527] shadow-[0_30px_60px_-20px_rgba(11,29,57,0.55)] ring-1 ring-black/10 font-mono text-[12.5px] leading-6">
    <figcaption className="flex items-center justify-between gap-4 px-5 py-3 border-b border-white/10 text-slate-400">
      <span><span className="font-semibold text-blue-300">POST</span> /apiCreateDocument</span>
      <span className="hidden sm:inline">api.bluecmr.com/sandbox/v1</span>
    </figcaption>
    <pre className="px-5 py-5 text-slate-200 overflow-x-auto">{REQUEST}</pre>
    <div className="px-5 py-3 border-y border-white/10 text-slate-400">
      <span className="font-semibold text-emerald-300">201</span> Created
    </div>
    <pre className="px-5 py-5 text-slate-200 overflow-x-auto">{RESPONSE}</pre>
  </figure>
);

const ProveedoresSoftwarePage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const primaryCta = usePrimaryCta(lang);
  const portal = DEVELOPER_PORTAL[lang];

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: PAGE_URL[lang] },
        ]}
        structuredData={[faqSchema(c.faq)]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          image={{ src: '/images/soluciones/hero-software-desarrolladores.webp', position: '10% 50%' }}
          primary={{ ...primaryCta, to: `${primaryCta.to}${QUERY}` }}
          secondary={{ href: portal, label: c.heroSecondary }}
          compact
        >
          <HeroChecks items={c.heroChecks} />
        </PageHero>

        <ItemsSection {...c.ways} tone="white" />

        {/* Una llamada, un documento */}
        <Section tone="gray">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.apiEyebrow} title={c.apiTitle} text={c.apiText} className="" />
              <CheckList items={c.apiItems} className="mt-8 space-y-2.5" />
              <TextLink href={portal} className="mt-8">{c.apiLink}</TextLink>
            </div>
            <div className="lg:col-span-7 min-w-0">
              <ApiSample />
            </div>
          </div>
        </Section>

        <ItemsSection {...c.built} tone="white" />
        <FlowSection {...c.process} tone="gray" />
        <FaqSection eyebrow={c.faqEyebrow} title={c.faqTitle} items={c.faq} tone="white" />

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} query={QUERY} />
      </div>
    </>
  );
};

export default ProveedoresSoftwarePage;
